// Callout placement (spec §10), apart from the DOM so it can be tested. Candidates go in priority
// order. Each tries eight positions round its anchor at four leader lengths, and takes the first that
// stays inside the frame's safe margin and out of the chrome (grown by the same margin), 8 px clear
// of every placed pill and anchor, with no leader across a pill. design/system/screens/screens.js
// places the screens' callouts the same way, so the app and the screens agree.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type Point = [number, number];

export type Dir = 'right' | 'left' | 'upper-right' | 'upper-left' | 'lower-right' | 'lower-left' | 'above' | 'below';
/** The spec's order: right, left, upper right, upper left, lower right, lower left, above, below. */
export const DIRS: readonly Dir[] = ['right', 'left', 'upper-right', 'upper-left', 'lower-right', 'lower-left', 'above', 'below'];
/** Leader lengths, short to long (`hud.leader` min 12, max 120). */
export const LENGTHS: readonly number[] = [12, 40, 80, 120];
/** A diagonal leader turns into an 8 px horizontal shelf at the pill: the sheet's one elbow. */
const SHELF = 8;
/** A pill holds its position while its anchor has moved less than this (spec §10, stability). */
export const HOLD_PX = 6;

export interface Candidate {
  id: string;
  /** The anchor in the overlay's CSS px. */
  x: number;
  y: number;
  /** The pill's size. */
  w: number;
  h: number;
  prio: number;
  /** Selected or hovered. If nothing clear is left, it may overlap other pills. */
  focus?: boolean;
}

export interface Placement {
  id: string;
  rect: Rect;
  /** Anchor, then the elbow if there is one, then the pill's edge. */
  path: Point[];
  dir: Dir;
  len: number;
  /** Where the anchor was when the pill was placed, for the hold. */
  ax: number;
  ay: number;
}

export interface Options {
  frame: { w: number; h: number };
  /** `size.safe-margin`: the frame's inset, and the chrome's growth. */
  margin: number;
  /** `hud.budget.gap`: clearance round placed pills and anchors. */
  gap: number;
  /** The chrome, already grown by the margin. */
  exclusions: readonly Rect[];
  /** What pills keep clear of though leaders and anchors may cross it: the electrode sites, each
   *  grown by its 2 px clearance (spec §15). */
  avoid?: readonly Rect[];
  limit: number;
  /**
   * The last solve's placements, by id:
   * - `hold` keeps pills that are still valid and whose anchors moved under HOLD_PX, then tries
   *   each one's old direction and length before the full search;
   * - `follow` (a drag) carries every old pill with its anchor at its old direction and length, and
   *   adds none;
   * - `fresh` is the spec's plain first fit, which the overlay runs once the picture is still.
   */
  prev?: ReadonlyMap<string, Placement>;
  mode?: 'hold' | 'follow' | 'fresh';
}

export const inflate = (r: Rect, m: number): Rect => ({ x: r.x - m, y: r.y - m, w: r.w + 2 * m, h: r.h + 2 * m });
const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const inside = (p: Point, r: Rect) => p[0] > r.x && p[0] < r.x + r.w && p[1] > r.y && p[1] < r.y + r.h;
const anchorBox = (p: Placement): Rect => ({ x: p.path[0]![0] - 4, y: p.path[0]![1] - 4, w: 8, h: 8 });

/** Whether segment pq meets rect r (Liang–Barsky). */
export function segHitsRect(p: Point, q: Point, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = q[0] - p[0];
  const dy = q[1] - p[1];
  const edges: [number, number][] = [
    [-dx, p[0] - r.x],
    [dx, r.x + r.w - p[0]],
    [-dy, p[1] - r.y],
    [dy, r.y + r.h - p[1]],
  ];
  for (const [pp, qq] of edges) {
    if (pp === 0) {
      if (qq < 0) return false;
    } else {
      const t = qq / pp;
      if (pp < 0) t0 = Math.max(t0, t);
      else t1 = Math.min(t1, t);
      if (t0 > t1) return false;
    }
  }
  return true;
}

/** The pill and leader for one direction and length. Diagonals run at 45° and end in the shelf. */
export function position(ax: number, ay: number, w: number, h: number, dir: Dir, len: number): { rect: Rect; path: Point[] } {
  const k = Math.max(0, (len - SHELF) / Math.SQRT2);
  switch (dir) {
    case 'right':
      return { rect: { x: ax + len, y: ay - h / 2, w, h }, path: [[ax, ay], [ax + len, ay]] };
    case 'left':
      return { rect: { x: ax - len - w, y: ay - h / 2, w, h }, path: [[ax, ay], [ax - len, ay]] };
    case 'upper-right':
      return { rect: { x: ax + k + SHELF, y: ay - k - h / 2, w, h }, path: [[ax, ay], [ax + k, ay - k], [ax + k + SHELF, ay - k]] };
    case 'upper-left':
      return { rect: { x: ax - k - SHELF - w, y: ay - k - h / 2, w, h }, path: [[ax, ay], [ax - k, ay - k], [ax - k - SHELF, ay - k]] };
    case 'lower-right':
      return { rect: { x: ax + k + SHELF, y: ay + k - h / 2, w, h }, path: [[ax, ay], [ax + k, ay + k], [ax + k + SHELF, ay + k]] };
    case 'lower-left':
      return { rect: { x: ax - k - SHELF - w, y: ay + k - h / 2, w, h }, path: [[ax, ay], [ax - k, ay + k], [ax - k - SHELF, ay + k]] };
    case 'above':
      return { rect: { x: ax - w / 2, y: ay - len - h, w, h }, path: [[ax, ay], [ax, ay - len]] };
    case 'below':
      return { rect: { x: ax - w / 2, y: ay + len, w, h }, path: [[ax, ay], [ax, ay + len]] };
  }
}

export function place(cands: readonly Candidate[], o: Options): Placement[] {
  const mode = o.mode ?? 'hold';
  const safe = { x: o.margin, y: o.margin, w: o.frame.w - 2 * o.margin, h: o.frame.h - 2 * o.margin };
  const placed: Placement[] = [];
  const inFrame = (r: Rect) => r.x >= safe.x && r.y >= safe.y && r.x + r.w <= safe.x + safe.w && r.y + r.h <= safe.y + safe.h;
  // No pill, leader or anchor inside the chrome.
  const offChrome = (r: Rect, path: Point[]) => !o.exclusions.some((e) => overlaps(r, e) || path.some((p) => inside(p, e)));
  const clear = (r: Rect, path: Point[]) => {
    const g = inflate(r, o.gap);
    if (placed.some((p) => overlaps(g, p.rect) || overlaps(g, anchorBox(p)))) return false;
    for (let i = 0; i + 1 < path.length; i++) if (placed.some((p) => segHitsRect(path[i]!, path[i + 1]!, inflate(p.rect, 2)))) return false;
    return !placed.some((p) => p.path.some((q, i) => i + 1 < p.path.length && segHitsRect(q, p.path[i + 1]!, r)));
  };
  const offSites = (r: Rect) => !o.avoid?.some((a) => overlaps(r, a));
  const valid = (r: Rect, path: Point[]) => inFrame(r) && offChrome(r, path) && offSites(r) && clear(r, path);

  for (const c of [...cands].sort((a, b) => b.prio - a.prio)) {
    if (placed.length >= o.limit) break;
    const prev = mode === 'fresh' ? undefined : o.prev?.get(c.id);
    const at = (dir: Dir, len: number): Placement => ({ id: c.id, ...position(c.x, c.y, c.w, c.h, dir, len), dir, len, ax: c.x, ay: c.y });
    let chosen: Placement | null = null;
    if (mode === 'follow') {
      // A drag doesn't re-plan: old pills ride with their anchors, and nothing new comes in. A pill
      // that rides into the chrome or off the frame drops out.
      const p = prev && at(prev.dir, prev.len);
      if (p && inFrame(p.rect) && offChrome(p.rect, p.path)) placed.push(p);
      continue;
    }
    if (prev && prev.rect.w === c.w && prev.rect.h === c.h && Math.hypot(c.x - prev.ax, c.y - prev.ay) < HOLD_PX) {
      const path: Point[] = [[c.x, c.y], ...prev.path.slice(1)];
      if (valid(prev.rect, path)) chosen = { ...prev, path };
    }
    if (!chosen && prev) {
      const p = at(prev.dir, prev.len);
      if (valid(p.rect, p.path)) chosen = p;
    }
    for (let l = 0; !chosen && l < LENGTHS.length; l++)
      for (const dir of DIRS) {
        const p = at(dir, LENGTHS[l]!);
        if (valid(p.rect, p.path)) {
          chosen = p;
          break;
        }
      }
    // The selected or hovered structure is named even if it has to overlap other pills, but never
    // over the chrome or off the frame.
    for (let l = 0; !chosen && c.focus && l < LENGTHS.length; l++)
      for (const dir of DIRS) {
        const p = at(dir, LENGTHS[l]!);
        if (inFrame(p.rect) && offChrome(p.rect, p.path) && offSites(p.rect)) {
          chosen = p;
          break;
        }
      }
    if (chosen) placed.push(chosen);
  }
  return placed;
}
